/**
 * Espace de travail actif — correction de UX-D0001 et UX-D0002.
 *
 * Avant : chaque écran demandait de TAPER un identifiant technique que le tableau de
 * bord affichait déjà. Une faute de frappe produisait un résultat vide sans indiquer
 * la cause. Le corpus `ux-research.md` pose que l'utilisateur ne doit jamais avoir à
 * mémoriser un identifiant technique.
 *
 * Après : la sélection est faite une fois, dans une liste alimentée par l'API, et
 * partagée par toute l'application.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
  type JSX, type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, type Workspace } from '../api/client'
import { useSession } from './useSession'

// L'espace choisi est PERSISTÉ (localStorage) : un rechargement ou une navigation par URL
// directe retrouve le projet sur lequel on travaillait, au lieu de retomber sur le premier
// espace de la liste (AUD-UX-02). La valeur n'est honorée que si elle désigne un espace
// réellement accessible — sinon on repli sur le premier, sans erreur.
const STORAGE_KEY = 'doit-active-workspace'

function lireEspaceStocke(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function ecrireEspaceStocke(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* stockage indisponible : la sélection reste valable en mémoire pour la session */
  }
}

/**
 * Un espace est ACCESSIBLE quand la passerelle y déclare une appartenance du porteur
 * (FND-DEMO-ADMIN-01 / HD-35). Sans `membership_role`, le garde refuserait chaque appel scoped :
 * l'espace reste listé (invitations, dépendances) mais n'est jamais un candidat à l'espace actif.
 */
export function estAccessible(w: Workspace): boolean {
  return typeof w.membership_role === 'string' && w.membership_role.length > 0
}

/** Un projet archivé est en lecture seule : il n'est proposé ni au choix courant ni à « rejoindre ». */
export function estArchive(w: Workspace): boolean {
  return w.status === 'archived'
}

interface WorkspaceContextValue {
  /** Tous les espaces de l'organisation, tels que listés par la passerelle. */
  workspaces: Workspace[]
  /** Sous-ensemble où le porteur est membre — seuls candidats à l'espace actif et aux sélecteurs. */
  accessibles: Workspace[]
  /**
   * Projets de l'organisation dont le porteur n'est PAS membre et qu'il peut REJOINDRE
   * (`POST /workspaces/{id}/join`, tracé) — exposés à l'administrateur de la plateforme SEUL
   * (ARB-SEC-01 : il voit tout, mais n'agit que par une appartenance réelle), archivés exclus.
   * Toujours vide pour tout autre porteur. Jamais candidats à l'espace actif tant qu'ils ne
   * sont pas rejoints.
   */
  aRejoindre: Workspace[]
  isLoading: boolean
  error: Error | null
  activeId: string | null
  active: Workspace | null
  setActiveId: (id: string | null) => void
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }): JSX.Element {
  // Restauré depuis le stockage : la sélection survit au rechargement.
  const [activeId, setActiveIdState] = useState<string | null>(() => lireEspaceStocke())
  const query = useQuery({ queryKey: ['workspaces'], queryFn: api.listWorkspaces })
  // Rôle de realm `admin` = administrateur de la plateforme (même lecture que `lib/nav.ts`).
  const adminPlateforme = Boolean(useSession()?.roles.includes('admin'))

  const setActiveId = useCallback((id: string | null) => {
    setActiveIdState(id)
    ecrireEspaceStocke(id)
  }, [])

  const value = useMemo<WorkspaceContextValue>(() => {
    const workspaces = query.data ?? []
    const accessibles = workspaces.filter(estAccessible)
    const aRejoindre = adminPlateforme
      ? workspaces.filter((w) => !estAccessible(w) && !estArchive(w))
      : []
    // On n'honore la sélection persistée que si elle désigne un espace RÉELLEMENT
    // accessible (il a pu être retiré, appartenir à un autre locataire, ou — HD-35 — être un
    // espace de l'organisation dont le porteur n'est pas membre). Sinon, repli sur le premier
    // espace ACCESSIBLE : ouvrir sur un état vide alors qu'un espace existe obligerait à un choix
    // sans enjeu. Aucun espace accessible ⇒ aucun espace actif : les hooks scoped restent
    // désactivés (`enabled: Boolean(activeId)`), jamais une cascade de 403.
    const connu = activeId && accessibles.some((w) => w.workspace_id === activeId)
      ? activeId : null
    const resolved = connu ?? accessibles[0]?.workspace_id ?? null
    return {
      workspaces,
      accessibles,
      aRejoindre,
      isLoading: query.isPending,
      error: (query.error as Error | null) ?? null,
      activeId: resolved,
      active: accessibles.find((w) => w.workspace_id === resolved) ?? null,
      setActiveId,
    }
  }, [query.data, query.isPending, query.error, activeId, setActiveId, adminPlateforme])

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext)
  if (!context) throw new Error('useWorkspace doit être utilisé dans un WorkspaceProvider')
  return context
}

/** Variante sans exigence de fournisseur : un composant d'appoint (libellé N28) s'en passe. */
export function useWorkspaceOptionnel(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext)
}
